import {CommonEndpoints} from "./common-endpoints";
import {HttpClient, HttpHeaders, HttpResponse} from "@angular/common/http";
import {BillingInformation, Order, TermsAndConditions, UTMParams} from "../../store/state/order.state";
import {Observable} from "rxjs";
import {environment} from "../../../environments/environment";
import {AdditionalQuestionsRequest, CalculatedPricing} from "./request-bodies";
import {OrderStatusChangedResponse} from "../../store/actions/order.actions";
import {ValidResourcesResponse} from "../../authentication/authentication.service";

export class ClientEndpoints extends CommonEndpoints {

	constructor(http: HttpClient) {
		super(http)
	}

	calculatePrice(order: Order): Observable<CalculatedPricing> {
		const url: string = `${environment.apiUrl}/client/order/${order.orderCorrelationId}/calculate-price`;
		return this.http.post<CalculatedPricing>(url, order, {headers: new HttpHeaders({"Content-Type": "application/json;charset=UTF-8"})})
	}

	changeOrder(order: Order): Observable<Order> {
		const url: string = `${environment.apiUrl}/client/order/${order.orderCorrelationId}/change-order`;
		return this.http.post<Order>(url, order, {headers: new HttpHeaders({"Content-Type": "application/json;charset=UTF-8"})});
	}

	changeAdditionalQuestions(correlationId: string, requests: AdditionalQuestionsRequest[]): Observable<HttpResponse<Order>> {
		const url: string = `${environment.apiUrl}/client/order/${correlationId}/change-additional-questions`;
		return this.http.post<Order>(url, requests, {
			headers: new HttpHeaders({"Content-Type": "application/json;charset=UTF-8"}),
			observe: "response"
		})
	}

	changeBillingInformation(correlationId: string, request: BillingInformation): Observable<Order> {
		const url: string = `${environment.apiUrl}/client/order/${correlationId}/change-billing-informations`;
		return this.http.post<Order>(url, request, {observe: "body", responseType: "json"});
	}

	requestOrder(order: Order, utmParams: UTMParams): Observable<HttpResponse<any>> {
		const url: string = `${environment.apiUrl}/client/order/request?email=${encodeURIComponent(order.contactPerson.email)}&firstName=${encodeURIComponent(order.contactPerson.firstName)}&lastName=${encodeURIComponent(order.contactPerson.lastName)}
		&UTMSource__c=${encodeURIComponent(utmParams.UTMSource__c)}&UTMMedium__c=${encodeURIComponent(utmParams.UTMMedium__c)}&UTMCampaign__c=${encodeURIComponent(utmParams.UTMCampaign__c)}`;
		return this.http.post<any>(url, order, {headers: new HttpHeaders({"Content-Type": "application/json;charset=UTF-8"}), observe: "response"});
	}

	requestEstimate(correlationId: string, order: Order): Observable<HttpResponse<any>> {
		const url: string = `${environment.apiUrl}/client/order/${correlationId}/request`;
		return this.http.post<any>(url, order, {headers: new HttpHeaders({"Content-Type": "application/json;charset=UTF-8"}), observe: "response"});
	}

	submitAdditionalQuestions(correlationId: string, requests: AdditionalQuestionsRequest[]) {
		const url: string = `${environment.apiUrl}/client/order/${correlationId}/additional-questions`;
		return this.http.post<any>(url, requests, {headers: new HttpHeaders({"Content-Type": "application/json;charset=UTF-8"}), observe: "response"});
	}

	confirmOrder(correlationId: string, termsAndConditions: TermsAndConditions): Observable<Order> {
		const url = `${environment.apiUrl}/client/order/${correlationId}/confirm-order`;
		return this.http.post<Order>(url, termsAndConditions, {
			headers: new HttpHeaders({
				"Content-Type": "application/json;charset=UTF-8"
			}), observe: "body"
		});
	}

	sendOrderToReview(correlationId: string, termsAndConditions: TermsAndConditions): Observable<Order> {
		const url = `${environment.apiUrl}/client/order/${correlationId}/send-to-review`;
		return this.http.post<Order>(url, termsAndConditions, {
			headers: new HttpHeaders({
				"Content-Type": "application/json;charset=UTF-8"
			}), observe: "body"
		});
	}

	uploadTransferDocuments(correlationId: string, certificateDocuments: File[], reportDocuments: File[]): Observable<Order> {
		const url: string = `${environment.apiUrl}/client/order/${correlationId}/upload-transfer-documents`;
		const formData = new FormData();
		certificateDocuments.forEach(document => {
			formData.append('certificateFiles', document);
		});
		reportDocuments.forEach(document => {
			formData.append('reportFiles', document);
		});
		return this.http.post<Order>(url, formData);
	}

	declineOrder(correlationId: string, reason: string): Observable<OrderStatusChangedResponse> {
		const url: string = `${environment.apiUrl}/client/order/${correlationId}/decline`;
		return this.http.post<OrderStatusChangedResponse>(url, reason);
	}

	acceptReviewerChanges(correlationId: string): Observable<OrderStatusChangedResponse> {
		const url: string = `${environment.apiUrl}/client/order/${correlationId}/accept-review-changes`;
		return this.http.post<OrderStatusChangedResponse>(url, null);
	}

	declineReviewerChanges(correlationId: string, reason: string): Observable<OrderStatusChangedResponse> {
		const url: string = `${environment.apiUrl}/client/order/${correlationId}/decline-review-changes`;
		return this.http.post<OrderStatusChangedResponse>(url, reason);
	}

	getValidOrders(clientEmail: string): Observable<ValidResourcesResponse> {
		return this.http.get<ValidResourcesResponse>(`${environment.apiUrl}/client/resource/valid?for-user=${encodeURIComponent(clientEmail)}`)
	}
}
